import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { pickEffectiveFloor } from "@/lib/roomFloor";
import { getVirtualTourEmbedSrc } from "@/lib/virtual-tour-url";
import { useParams, Link, useNavigate } from "react-router-dom";
import posthog from "posthog-js";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  MapPin,
  Users,
  Wifi,
  UtensilsCrossed,
  Bath,
  Droplets,
  Shirt,
  Monitor,
  Sofa,
  Coffee,
  ChevronLeft,
  ChevronRight,
  X,
  Heart,
  Maximize2,
  Share2,
  Check,
  CalendarClock,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { baseLanguageFor } from "@/lib/i18n/languages";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";
import {
  getDisplayMonthlyRentEURFromBookingAndPayments,
  getExpectedDepositEUR,
  getFullMonthlyRentFromBooking,
} from "@/lib/rent";
import { resolvePublicStreet } from "@/lib/address";
import RoomLocationMap from "@/components/RoomLocationMap";
import { useMoreRoomsInFlat } from "@/components/MoreRoomsInFlat";
import { firstImageUrl } from "@/lib/roomImages";
import RoomThingsToKnow from "@/components/RoomThingsToKnow";
import MoreRoomsInFlat from "@/components/MoreRoomsInFlat";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import room1 from "@/assets/room-1.jpg";
import room2 from "@/assets/room-2.jpg";
import room3 from "@/assets/room-3.jpg";
import { getImageUrl, getImageSrcSet } from "@/lib/imageUrl";
import { PhotoFrame } from "@/components/properties/PhotoFrame";
import {
  RoomHeader,
  RoomGallery,
  RoomFacts,
  type RoomFact,
  RoomSection,
  RoomFeatureList,
  RoomSteps,
  RoomBookingCard,
  RoomSiblings,
  RoomLocation,
  type SiblingRoom,
} from "@/components/rooms/RoomDetailSections";
import { hasPlottableCoordinates } from "@/lib/coordinates";
import { MAPBOX_TOKEN } from "@/config/mapbox";
import { INK, MUTED, SAND, LINE, IMAGE_BG } from "@/lib/marketing-theme";
import { SmoothImage } from "@/components/ui/smooth-image";
import { FlatmateChips, type Flatmate } from "@/components/rooms/FlatmateChips";
import { Skeleton } from "@/components/ui/skeleton";
import {
  canApplyToRoom,
  getListingAvailability,
  formatNextAvailableDate,
  resolveAvailableFrom,
} from "@/lib/roomAvailability";

// Render widths per context, picked from the rendered CSS box × 2 for retina.
// Main image is an aspect-[4/3] PhotoFrame inside lg:col-span-2 (~800px wide
// on desktop). Photo boxes are sized by ASPECT RATIO, never a fixed pixel
// height: a fixed height makes the box shape depend on the viewport, and the
// portrait room photos then got sliced by object-cover (looked zoomed in).
// Thumbs are aspect-square in a 6-col grid (~130px each) — cover is right there.
// Lightbox max-w-7xl max-h-[90vh] (~1280px wide).
// About-building carousel is an aspect-[4/3] PhotoFrame, full-width in col-span-2.
const HERO_WIDTH = 1200;
const HERO_SRCSET = [800, 1600];
const HERO_SIZES = "(max-width: 1024px) 100vw, 66vw";
const THUMB_WIDTH = 200;
const THUMB_SRCSET = [200, 400];
const LIGHTBOX_WIDTH = 2400;

const LIKED_ROOMS_KEY = "property-detail-liked-rooms";

const DEFAULT_AMENITY_LABELS = [
  "Wifi available",
  "Shared Kitchen",
  "Shared Bathroom",
  "Shared WC",
  "Shared Laundry",
  "Private desk",
  "Fully furnished",
];

/** Labels not taken from CMS when we need fixed wording (e.g. shared amenities, wifi). */
const SHARED_AMENITY_TRANSLATION_KEYS = new Set([
  "property.amenity.wifiAvailable",
  "property.amenity.highSpeedWifi", // legacy Storyblok / cache keys
  "property.amenity.kitchen",
  "property.amenity.bathroom",
  "property.amenity.wc",
  "property.amenity.laundry",
]);

function parseHouseFeaturesJson(raw: unknown): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw
      .filter((x): x is string => typeof x === "string" && x.trim().length > 0)
      .map((s) => s.trim());
  }
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) {
        return p
          .filter(
            (x): x is string => typeof x === "string" && x.trim().length > 0
          )
          .map((s) => s.trim());
      }
    } catch {
      const t = raw.trim();
      return t ? [t] : [];
    }
  }
  return [];
}

/** CMS often stores "High-speed internet"; treat as the same feature as wifi for display. */
function isWifiRelatedLabel(label: string): boolean {
  const lower = label.toLowerCase();
  return (
    lower.includes("wifi") ||
    lower.includes("wlan") ||
    lower.includes("high-speed") ||
    lower.includes("high speed") ||
    (lower.includes("internet") &&
      (lower.includes("speed") ||
        lower.includes("wi-fi") ||
        lower.includes("wifi") ||
        lower.includes("wlan")))
  );
}

function formatEuroAmount(n: number, language: "en" | "de"): string {
  const locale = language === "de" ? "de-DE" : "en-US";
  return Number(n).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const PropertyDetail = () => {
  const { id } = useParams();
  const { t, language: activeLanguage } = useLanguage();
  // COL-478: this surface still carries inline de/en copy (swept in COL-479);
  // tenants in the four new languages read the English half.
  const language = baseLanguageFor(activeLanguage);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [localLiked, setLocalLiked] = useState(false);
  const [isLikeAnimating, setIsLikeAnimating] = useState(false);
  const [aboutCarouselIndex, setAboutCarouselIndex] = useState(0);
  const [aboutCarouselApi, setAboutCarouselApi] = useState<CarouselApi | null>(
    null
  );
  const virtualTourContainerRef = useRef<HTMLDivElement>(null);
  const [isTourFullscreen, setIsTourFullscreen] = useState(false);

  // Fetch user's favorites from Supabase (when logged in) so like status matches Properties page
  const { data: favoritesData } = useQuery({
    queryKey: ["favorites", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("favorites")
        .select("room_id")
        .eq("user_id", user.id);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  const favoritedRoomIds = useMemo(() => {
    if (!favoritesData) return new Set<number>();
    return new Set(favoritesData.map((f: { room_id: number }) => f.room_id));
  }, [favoritesData]);

  // Liked state: when logged in use Supabase; when guest use localStorage (keyed by room id from URL)
  const isLiked = user ? favoritedRoomIds.has(Number(id)) : localLiked;

  // Persist liked state in localStorage for guests only
  useEffect(() => {
    if (!id || user) return;
    try {
      const raw = localStorage.getItem(LIKED_ROOMS_KEY);
      const set = raw ? (JSON.parse(raw) as Set<string>) : new Set<string>();
      setLocalLiked(set.has(id));
    } catch {
      setLocalLiked(false);
    }
  }, [id, user]);

  const toggleFavoriteMutation = useMutation({
    mutationFn: async ({
      roomId,
      isFavorited,
    }: {
      roomId: number;
      isFavorited: boolean;
    }) => {
      if (!user?.id) throw new Error("Please log in to favorite properties");
      if (isFavorited) {
        const { error } = await supabase
          .from("favorites")
          .delete()
          .eq("user_id", user.id)
          .eq("room_id", roomId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("favorites")
          .insert([{ user_id: user.id, room_id: roomId }]);
        if (error) throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["favorites", user?.id] });
      // Keep localStorage in sync so guest→user transition or other tabs stay consistent
      try {
        const raw = localStorage.getItem(LIKED_ROOMS_KEY);
        const set = raw
          ? new Set(JSON.parse(raw) as string[])
          : new Set<string>();
        if (variables.isFavorited) set.delete(String(variables.roomId));
        else set.add(String(variables.roomId));
        localStorage.setItem(LIKED_ROOMS_KEY, JSON.stringify([...set]));
      } catch {
        /* ignore localStorage / JSON errors */
      }
    },
  });

  const toggleLike = useCallback(() => {
    if (!id) return;
    const roomId = Number(id);
    setIsLikeAnimating(true);
    setTimeout(() => setIsLikeAnimating(false), 350);

    if (user) {
      toggleFavoriteMutation.mutate({
        roomId,
        isFavorited: favoritedRoomIds.has(roomId),
      });
      return;
    }

    setLocalLiked((prev) => {
      const next = !prev;
      try {
        const raw = localStorage.getItem(LIKED_ROOMS_KEY);
        const set = raw
          ? new Set(JSON.parse(raw) as string[])
          : new Set<string>();
        if (next) set.add(id);
        else set.delete(id);
        localStorage.setItem(LIKED_ROOMS_KEY, JSON.stringify([...set]));
      } catch {
        /* ignore localStorage / JSON errors */
      }
      return next;
    });
  }, [id, user, favoritedRoomIds, toggleFavoriteMutation]);

  // Fallback translations so German (and English) work when Storyblok cache is empty
  const fallbackTranslations: Record<string, Record<string, string>> = {
    en: {
      "property.back": "Back to Properties",
      "property.roomNotFound": "Room not found",
      "property.backToProperties": "Back to Properties",
      "property.currentlyUnavailable": "Currently Unavailable",
      "property.roomDetails": "Room Details",
      "property.roommatesCount": "roommates",
      "property.aboutBuilding": "About {0}",
      "properties.unavailable": "Unavailable",
      "property.like": "Like",
      "property.liked": "Liked",
      "property.amenities": "Amenities",
      "property.roommates": "Roommates",
      "properties.perMonth": "per month",
      "properties.available": "Available",
      "property.apply": "Apply for the Room",
      "property.applyAlready": "Room Already Applied",
      "property.toastAlreadyApplied": "You have already applied for this room.",
      "booking.costOverview": "Cost overview",
      "booking.monthlyRent": "Monthly rent",
      "booking.deposit": "Deposit",
      "booking.chargedAfterSigning": "Charged after signing",
      "booking.oneTime": "One-time",
      "property.house": "House features",
      "property.houseFeature.wifi": "Wifi available",
      "property.houseFeature.kitchen": "Shared kitchen",
      "property.houseFeature.furnished": "Fully furnished",
      "property.amenity.wifiAvailable": "Wifi available",
      "property.amenity.highSpeedWifi": "Wifi available",
      "property.amenity.kitchen": "Shared Kitchen",
      "property.amenity.bathroom": "Shared Bathroom",
      "property.amenity.wc": "Shared WC",
      "property.amenity.laundry": "Shared Laundry",
      "property.amenity.privateDesk": "Private desk",
      "property.amenity.fullyFurnished": "Fully furnished",
      "property.breadcrumbAriaLabel": "Breadcrumb",
      "property.previousImageAriaLabel": "Previous image",
      "property.nextImageAriaLabel": "Next image",
      "property.closeLightboxAriaLabel": "Close lightbox",
      "property.virtualTourTitle": "360° Virtual Tour",
      "property.virtualTourHeading": "360° Virtual Tour",
      "property.imageAlt": "Image",
      "property.exitFullscreen": "Exit fullscreen",
      "property.viewFullscreen": "View fullscreen",
      "property.virtualTourComingSoon": "360° Tour Coming Soon",
    },
    de: {
      "property.back": "Zurück zu den Objekten",
      "property.roomNotFound": "Zimmer nicht gefunden",
      "property.backToProperties": "Zurück zu den Objekten",
      "property.currentlyUnavailable": "Derzeit nicht verfügbar",
      "property.roomDetails": "Zimmerdetails",
      "property.roommatesCount": "Mitbewohner",
      "property.aboutBuilding": "Über {0}",
      "properties.unavailable": "Nicht verfügbar",
      "property.like": "Gefällt mir",
      "property.liked": "Gefällt mir",
      "property.amenities": "Ausstattung",
      "property.roommates": "Mitbewohner",
      "properties.perMonth": "pro Monat",
      "properties.available": "Verfügbar",
      "property.apply": "Für Zimmer bewerben",
      "property.applyAlready": "Bereits beworben",
      "property.toastAlreadyApplied":
        "Sie haben sich bereits für dieses Zimmer beworben.",
      "booking.costOverview": "Kostenüberblick",
      "booking.monthlyRent": "Monatsmiete",
      "booking.deposit": "Kaution",
      "booking.chargedAfterSigning": "Nach Unterzeichnung fällig",
      "booking.oneTime": "Einmalig",
      "property.house": "Ausstattung",
      "property.houseFeature.wifi": "WLAN verfügbar",
      "property.houseFeature.kitchen": "Gemeinschaftsküche",
      "property.houseFeature.furnished": "Voll möbliert",
      "property.amenity.wifiAvailable": "WLAN verfügbar",
      "property.amenity.highSpeedWifi": "WLAN verfügbar",
      "property.amenity.kitchen": "Gemeinschaftsküche",
      "property.amenity.bathroom": "Gemeinschaftsbad",
      "property.amenity.wc": "Gemeinschafts-WC",
      "property.amenity.laundry": "Gemeinschaftliche Waschküche",
      "property.amenity.privateDesk": "Eigener Schreibtisch",
      "property.amenity.fullyFurnished": "Voll möbliert",
      "property.breadcrumbAriaLabel": "Brotkrumen-Navigation",
      "property.previousImageAriaLabel": "Vorheriges Bild",
      "property.nextImageAriaLabel": "Nächstes Bild",
      "property.closeLightboxAriaLabel": "Lightbox schließen",
      "property.virtualTourTitle": "360°-Rundgang",
      "property.virtualTourHeading": "360°-Rundgang",
      "property.imageAlt": "Bild",
      "property.exitFullscreen": "Vollbild beenden",
      "property.viewFullscreen": "Vollbild anzeigen",
      "property.virtualTourComingSoon": "360°-Rundgang folgt in Kürze",
    },
  };

  const getTranslation = useCallback(
    (key: string, placeholder?: string): string => {
      const raw = t(key)?.trim();
      // Some environments return the key itself when missing; treat that as "not translated".
      const translation = raw && raw !== key ? raw : "";
      const fallback =
        fallbackTranslations[language]?.[key] ??
        fallbackTranslations["en"][key] ??
        "";
      // Never show raw keys in UI. Prefer translation → fallback; otherwise empty.
      const text = translation || fallback;
      if (typeof placeholder !== "undefined" && text.includes("{0}")) {
        return text.replace("{0}", placeholder);
      }
      return text;
    },
    [t, language]
  );

  const getAmenityLabel = useCallback(
    (key: string) => {
      if (SHARED_AMENITY_TRANSLATION_KEYS.has(key)) {
        return (
          fallbackTranslations[language]?.[key] ??
          fallbackTranslations["en"][key] ??
          key
        );
      }
      return getTranslation(key);
    },
    [language, getTranslation]
  );

  /** Prefer local fallbacks for wifi copy so outdated CMS cache cannot show "High-speed internet". */
  const getWifiHouseFeatureLabel = useCallback(() => {
    const key = "property.houseFeature.wifi";
    return (
      fallbackTranslations[language]?.[key] ??
      fallbackTranslations["en"][key] ??
      getTranslation(key)
    );
  }, [language, getTranslation]);

  // Fetch ROOM from Supabase (with property info)
  const {
    data: roomData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["room", id],
    queryFn: async () => {
      if (!id) throw new Error("Room ID is required");

      const { data: room, error: roomError } = await supabase
        .from("rooms")
        .select(
          `
          *,
          properties (
            id,
            title,
            city,
            address,
            street_name,
            lat,
            lng,
            description,
            description_translations,
            image_url,
            images,
            virtual_tour_url,
            pickup_method
          )
        `
        )
        .eq("id", Number(id))
        .single();

      if (roomError) throw roomError;
      if (!room) throw new Error("Room not found");

      return room;
    },
    enabled: !!id,
  });

  // The effective (apartment-overridden) virtual tour URL, resolved server-side
  // (COL-438): `apartments` has no public SELECT policy — it carries financial/
  // owner data — so a client-side embed always reads NULL for this unauthenticated
  // page. get_room_virtual_tour_url exposes only this one field via a narrow
  // SECURITY DEFINER resolver instead of opening up the apartments table.
  const { data: virtualTourUrl = null } = useQuery({
    queryKey: ["room-virtual-tour-url", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase.rpc("get_room_virtual_tour_url", {
        p_room_id: Number(id),
      });
      if (error) throw error;
      return (data as string | null) ?? null;
    },
    enabled: !!id,
  });
  // Stored links are often the "watch" form, which YouTube/Vimeo refuse to
  // frame — rewrite to the embed form (and drop non-https values) at render.
  const virtualTourSrc = getVirtualTourEmbedSrc(virtualTourUrl);

  // The effective floor, same COL-438 shape: the floor really belongs to the
  // apartment, and `apartments` has no public SELECT policy, so this page can
  // only ever see a room's own `rooms.floor`. On live that is 47 of 86 visible
  // rooms, while 13 of the 39 blank ones sit in an apartment that does have a
  // floor — this resolver is how the page reaches those.
  const { data: resolvedFloor = null } = useQuery({
    queryKey: ["room-floor", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase.rpc("get_room_floor", {
        p_room_id: Number(id),
      });
      if (error) throw error;
      return (data as string | null) ?? null;
    },
    enabled: !!id,
  });

  // Who already lives in this flat (COL-530). Same shape again: the answer
  // joins bookings → profiles, neither of which an anonymous visitor may
  // read, so the resolver is the only path. It returns age + gender and
  // nothing else — see 20261012120100_get_room_flatmates.sql.
  const { data: flatmates = [] } = useQuery({
    queryKey: ["room-flatmates", id],
    queryFn: async () => {
      if (!id) return [] as Flatmate[];
      const { data, error } = await supabase.rpc("get_room_flatmates", {
        p_room_id: Number(id),
      });
      if (error) throw error;
      return (data as Flatmate[] | null) ?? [];
    },
    enabled: !!id,
  });

  // Effective key pickup method and reminders setting, same COL-438 shape as
  // the virtual tour URL above: both inherit from `apartments`, which has no
  // public SELECT policy, so these narrow SECURITY DEFINER resolvers are the
  // only way this unauthenticated page can see the apartment-level override.
  const { data: effectivePickupMethod = null } = useQuery({
    queryKey: ["room-pickup-method", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase.rpc("get_room_pickup_method", {
        p_room_id: Number(id),
      });
      if (error) throw error;
      return (data as "keynest" | "manual" | null) ?? null;
    },
    enabled: !!id,
  });

  const {
    data: remindersEnabledResolved,
    isFetched: remindersFetched,
    isError: remindersErrored,
  } = useQuery({
    queryKey: ["room-reminders-enabled", id],
    queryFn: async () => {
      if (!id) return true;
      const { data, error } = await supabase.rpc("get_room_reminders_enabled", {
        p_room_id: Number(id),
      });
      if (error) throw error;
      return (data as boolean | null) ?? true;
    },
    enabled: !!id,
  });

  // Neutral copy is the safe default while this hasn't resolved yet (or
  // failed): the whole point of this resolver is to never claim a reminder
  // the page hasn't actually confirmed, so "unknown" must read the same as
  // "disabled", not the same as "enabled".
  const remindersEnabled =
    remindersFetched && !remindersErrored
      ? remindersEnabledResolved !== false
      : false;

  // Check if the current user has already applied for this room
  const { data: existingBooking } = useQuery({
    queryKey: ["booking-for-room", user?.id, id],
    queryFn: async () => {
      if (!user?.id || !id) return null;
      const { data, error } = await supabase
        .from("bookings")
        .select(
          "id, monthly_rent, deposit_amount, status, start_date, move_in_date"
        )
        .eq("user_id", user.id)
        .eq("room_id", Number(id))
        .is("admin_archived_at", null)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && !!id,
  });

  const { data: bookingRentPayments = [] } = useQuery({
    queryKey: ["booking-rent-payments-pricing", existingBooking?.id],
    queryFn: async () => {
      if (!existingBooking?.id) return [];
      const { data, error } = await supabase
        .from("payments")
        .select("amount_cents, gross_amount_cents, due_date")
        .eq("booking_id", existingBooking.id)
        .eq("type", "rent");
      if (error) throw error;
      return data || [];
    },
    enabled: !!existingBooking?.id,
  });

  const hasAlreadyApplied = !!existingBooking;

  // Transform room data (with property context)
  const room = roomData
    ? (() => {
        const property = roomData.properties;

        // Fix double-prefixed Storyblok URLs from sync (e.g. https://a.storyblok.com/f/https://a.storyblok.com/f/...)
        const fixStoryblokImageUrl = (url: string): string => {
          const double = "https://a.storyblok.com/f/https://a.storyblok.com/";
          if (url.startsWith(double))
            return "https://a.storyblok.com/" + url.slice(double.length);
          return url;
        };

        // Parse room images - handle JSONB format from Supabase (same logic as admin so 4+ images show)
        const roomImages = (() => {
          if (roomData.images) {
            try {
              let imgArray: any[] = [];
              if (typeof roomData.images === "string") {
                imgArray = JSON.parse(roomData.images);
              } else if (Array.isArray(roomData.images)) {
                imgArray = roomData.images;
              } else if (typeof roomData.images === "object") {
                imgArray = Object.values(roomData.images);
              }
              if (imgArray.length === 0) return [];
              const urls: string[] = [];
              for (const img of imgArray) {
                if (!img) continue;
                let url: string | null = null;
                if (typeof img === "string") {
                  url = img.startsWith("http")
                    ? img
                    : img.startsWith("f/")
                      ? `https://a.storyblok.com/${img}`
                      : `https://a.storyblok.com/f/${img}`;
                } else if (typeof img === "number") {
                  url = `https://a.storyblok.com/f/${img}`;
                } else if (typeof img === "object" && img !== null) {
                  if (typeof (img as any).url === "string")
                    url = (img as any).url;
                  else if (typeof (img as any).filename === "string") {
                    const f = (img as any).filename;
                    url = f.startsWith("http")
                      ? f
                      : f.startsWith("f/")
                        ? `https://a.storyblok.com/${f}`
                        : `https://a.storyblok.com/f/${f}`;
                  }
                }
                if (url) urls.push(fixStoryblokImageUrl(url));
              }
              const filtered = urls.filter(Boolean);
              return filtered;
            } catch (error) {
              console.error(
                "Error parsing room images:",
                error,
                roomData.images
              );
            }
          }
          return [];
        })();

        // Add property image to the gallery (only for property context, not for room display)
        const propertyImageRaw = property?.image_url
          ? property.image_url.startsWith("http")
            ? property.image_url
            : property.image_url.startsWith("f/")
              ? `https://a.storyblok.com/${property.image_url}`
              : `https://a.storyblok.com/f/${property.image_url}`
          : null;
        const propertyImage = propertyImageRaw
          ? fixStoryblokImageUrl(propertyImageRaw)
          : null;

        // Parse property.images (array from sync: Image, Image2, Image3) for About Villa carousel
        const propertyImages: string[] = (() => {
          const raw = (property as { images?: unknown })?.images;
          if (!raw) return [];
          const arr: unknown[] = Array.isArray(raw)
            ? raw
            : typeof raw === "string"
              ? (() => {
                  try {
                    return JSON.parse(raw);
                  } catch {
                    return [];
                  }
                })()
              : [];
          const out = arr
            .filter((u): u is string => typeof u === "string" && u.length > 0)
            .map((u) =>
              u.startsWith("http")
                ? u
                : u.startsWith("f/")
                  ? `https://a.storyblok.com/${u}`
                  : `https://a.storyblok.com/f/${u}`
            )
            .map(fixStoryblokImageUrl)
            .filter((u): u is string => !!u);
          return out;
        })();

        // Gallery: the room's own photos first, then the building's — the
        // latter are what the old page showed under "About this home", and
        // without them a room with few photos fell through to three bundled
        // stock shots that are not this flat at all.
        const gallery = [...roomImages, ...propertyImages, propertyImage]
          .filter((u): u is string => typeof u === "string" && u.length > 0)
          .filter((u, i, arr) => arr.indexOf(u) === i);
        const allImages: (string | any)[] =
          gallery.length > 0 ? gallery : [room1, room2, room3];

        // Determine main image - prefer room image, then property, then fallback
        const mainImage =
          roomImages.length > 0 ? roomImages[0] : propertyImage || room1;

        // Resolve room and property descriptions by current language (EN/DE from description_translations)
        const asTranslations = (v: unknown): Record<string, string> | null => {
          if (v == null) return null;
          if (typeof v === "object" && v !== null && !Array.isArray(v))
            return v as Record<string, string>;
          if (typeof v === "string") {
            try {
              const parsed = JSON.parse(v) as Record<string, string>;
              return parsed && typeof parsed === "object" ? parsed : null;
            } catch {
              return null;
            }
          }
          return null;
        };
        const roomDescTranslations = asTranslations(
          (roomData as { description_translations?: unknown })
            .description_translations
        );
        const propDescTranslations = asTranslations(
          (property as { description_translations?: unknown } | null)
            ?.description_translations
        );
        const roomDescByLang =
          roomDescTranslations &&
          (roomDescTranslations[language] ||
            roomDescTranslations.en ||
            roomDescTranslations.de);
        const propDescByLang =
          propDescTranslations &&
          (propDescTranslations[language] ||
            propDescTranslations.en ||
            propDescTranslations.de);
        const roomDescription =
          roomDescByLang ||
          roomData.description ||
          property?.description ||
          "Beautiful furnished room in a modern co-living space.";
        const propertyDescription =
          propDescByLang || property?.description || "";

        const houseFeatureLabels = parseHouseFeaturesJson(
          (roomData as { house_features?: unknown }).house_features
        );
        const amenitiesList =
          houseFeatureLabels.length > 0
            ? houseFeatureLabels
            : DEFAULT_AMENITY_LABELS;
        const roommatesCountRaw = (
          roomData as { roommates_count?: number | null }
        ).roommates_count;
        const roommatesCount =
          roommatesCountRaw != null && !Number.isNaN(Number(roommatesCountRaw))
            ? Number(roommatesCountRaw)
            : null;

        const countryLabel = language === "de" ? "Deutschland" : "Germany";
        // COL-218: hide house numbers on public pages. Derive the street from
        // the admin-maintained `address` column (see resolvePublicStreet); the
        // legacy contract `street_name` field is only a fallback because the V2
        // admin never updates it and it drifts from the real address.
        const publicStreet = resolvePublicStreet(property);
        const fullAddressLine = [publicStreet, property?.city, countryLabel]
          .filter(Boolean)
          .join(", ");

        return {
          id: roomData.id,
          roomId: roomData.id,
          propertyId: property?.id,
          title: roomData.title, // Room title
          buildingName: property?.title || "Property", // Building name
          city: property?.city || "",
          address: publicStreet,
          fullAddressLine,
          price: Number(roomData.price) || 0,
          // Both already exist on `rooms` and are editable in the admin
          // room sheet; they were simply never surfaced publicly.
          floor: (roomData as { floor?: string | null }).floor ?? null,
          image: mainImage,
          images: allImages,
          amenities: amenitiesList,
          roommatesCount,
          houseFeatureLabels,
          description: roomDescription,
          propertyDescription,
          available: roomData.available !== false,
          // Admin-owned `available_from` first; the legacy mirror is only a
          // fallback for rows written before the backfill.
          availableFrom: resolveAvailableFrom(
            roomData as {
              available_from?: string | null;
              next_available_date?: string | null;
            }
          ),
          apartmentId:
            (roomData as { apartment_id?: number | null }).apartment_id ?? null,
          // Effective key pickup method, resolved server-side via
          // get_room_pickup_method (room → apartment → property → 'keynest',
          // matching resolveEffectiveKeyPickup) — see the query above.
          pickupMethod: effectivePickupMethod,
          remindersEnabled,
          // Property context — `address` carries the redacted public value
          // (COL-218); map links use lat/lng so they're unaffected.
          property: {
            id: property?.id,
            title: property?.title,
            city: property?.city,
            address: publicStreet,
            lat: property?.lat,
            lng: property?.lng,
            description: propertyDescription,
            image: propertyImage,
            images:
              propertyImages.length > 0
                ? propertyImages
                : propertyImage
                  ? [propertyImage]
                  : [],
            virtual_tour_url: (property as any)?.virtual_tour_url ?? null,
          },
        };
      })()
    : null;

  // "Weitere Zimmer in dieser WG." — same rows MoreRoomsInFlat picks, so the
  // apartment scoping, the same-city fallback and the visibility filter stay in
  // one place rather than being re-implemented against property_id here.
  const { rooms: siblingRooms, isCityFallback } = useMoreRoomsInFlat({
    roomId: Number(id),
    apartmentId:
      (roomData as { apartment_id?: number | null } | null)?.apartment_id ??
      null,
    city: roomData?.properties?.city ?? "",
  });

  // Shared image navigation (used by arrows, keyboard, and swipe).
  const imageCount = room?.images?.length ?? 0;
  const goToPrevImage = useCallback(() => {
    if (imageCount <= 1) return;
    setCurrentImageIndex((prev) => (prev === 0 ? imageCount - 1 : prev - 1));
  }, [imageCount]);
  const goToNextImage = useCallback(() => {
    if (imageCount <= 1) return;
    setCurrentImageIndex((prev) => (prev === imageCount - 1 ? 0 : prev + 1));
  }, [imageCount]);

  // Touch swipe: record the start X and act on release if it passed a threshold.
  const touchStartX = useRef<number | null>(null);
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  }, []);
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const endX = e.changedTouches[0]?.clientX ?? touchStartX.current;
      const dx = endX - touchStartX.current;
      touchStartX.current = null;
      if (Math.abs(dx) < 40) return; // ignore taps / tiny drags
      if (dx > 0) goToPrevImage();
      else goToNextImage();
    },
    [goToPrevImage, goToNextImage]
  );

  // Share the current room (native share sheet, else copy link to clipboard).
  const [shareCopied, setShareCopied] = useState(false);
  const handleShare = useCallback(async () => {
    const url = window.location.href;
    const title = room?.title ?? "coliviq";
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
    } catch (err) {
      // User dismissed the share sheet — nothing more to do.
      if (err instanceof Error && err.name === "AbortError") return;
      // Any other share failure (e.g. NotAllowedError): fall through and try
      // copying the link to the clipboard instead of silently doing nothing.
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      toast.success(
        language === "de" ? "Link kopiert" : "Link copied to clipboard"
      );
      window.setTimeout(() => setShareCopied(false), 2000);
    } catch {
      toast.error(
        language === "de" ? "Kopieren fehlgeschlagen" : "Copy failed"
      );
    }
  }, [room?.title, language]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!isLightboxOpen || imageCount <= 1) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        goToPrevImage();
      } else if (e.key === "ArrowRight") {
        goToNextImage();
      } else if (e.key === "Escape") {
        setIsLightboxOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLightboxOpen, imageCount, goToPrevImage, goToNextImage]);

  // Reset image index when room changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [id]);

  // Track property view — intentionally depends only on room?.id to fire once per room navigation
  useEffect(() => {
    if (!room) return;
    posthog.capture("property viewed", {
      room_id: room.id,
      room_title: room.title,
      city: room.city,
      price: room.price,
      available: room.available,
    });
  }, [room?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync About Villa carousel dots with selected slide
  useEffect(() => {
    if (!aboutCarouselApi) return;
    const onSelect = () =>
      setAboutCarouselIndex(aboutCarouselApi.selectedScrollSnap());
    onSelect();
    aboutCarouselApi.on("select", onSelect);
    return () => {
      aboutCarouselApi.off("select", onSelect);
    };
  }, [aboutCarouselApi]);

  // Keep fullscreen button label in sync (e.g. when user presses Escape)
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsTourFullscreen(
        document.fullscreenElement === virtualTourContainerRef.current
      );
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen pb-20">
        {/* Hero banner placeholder */}
        <Skeleton
          className="w-full rounded-none"
          style={{ height: "58vh", minHeight: 420 }}
        />
        <div className="container px-4 pt-10">
          <Skeleton className="mb-10 h-24 w-full max-w-3xl rounded-2xl" />
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="space-y-8 lg:col-span-2">
              <Skeleton className="aspect-[4/3] w-full rounded-3xl" />
              <Skeleton className="h-48 w-full rounded-2xl" />
              <Skeleton className="h-64 w-full rounded-2xl" />
            </div>
            <div>
              <Skeleton className="h-[420px] w-full rounded-3xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !room) {
    return (
      <div className="min-h-screen pt-24 pb-20">
        <div className="container px-4">
          <div className="text-center">
            <p className="text-red-500 mb-4">
              {getTranslation("property.roomNotFound")}
            </p>
            <Button onClick={() => navigate("/rooms")}>
              <ArrowLeft className="mr-2 w-4 h-4" />
              {getTranslation("property.backToProperties")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const bookingForPricing = {
    monthly_rent: existingBooking?.monthly_rent,
    deposit_amount: existingBooking?.deposit_amount,
    rooms: { price: room.price },
  };
  const preferRentMonthYm =
    String(existingBooking?.move_in_date || existingBooking?.start_date || "")
      .trim()
      .slice(0, 7) || null;
  const displayMonthly =
    existingBooking?.id != null && bookingRentPayments.length > 0
      ? getDisplayMonthlyRentEURFromBookingAndPayments(bookingForPricing, {
          rentPayments: bookingRentPayments,
          preferCalendarMonthYm: preferRentMonthYm,
        })
      : getFullMonthlyRentFromBooking(bookingForPricing);
  const displayDeposit = getExpectedDepositEUR({
    monthly_rent: displayMonthly,
    deposit_amount: existingBooking?.deposit_amount,
    rooms: { price: room.price },
  });

  // Effective listing availability + move-in date to surface near the price.
  // The date only ever comes from a confirmed move-out (`available_from`); a
  // taken room without one says "Available soon" rather than naming a day
  // nobody has agreed to.
  const availabilityInput = {
    available: room.available,
    available_from: room.availableFrom,
  };
  const { listingAvailable, nextAvailableDate } =
    getListingAvailability(availabilityInput);
  // Applying is open while the room is free OR has a confirmed date — the
  // booking form only lets the applicant pick a move-in on/after that date.
  const canApply = canApplyToRoom(availabilityInput);
  const moveInLabel = listingAvailable
    ? language === "de"
      ? "Sofort bezugsfertig"
      : "Available now"
    : nextAvailableDate
      ? `${language === "de" ? "Verfügbar ab" : "Available from"} ${formatNextAvailableDate(nextAvailableDate, language)}`
      : language === "de"
        ? "Demnächst verfügbar"
        : "Available soon";

  const sidebarHouseFeatures =
    room.houseFeatureLabels.length > 0
      ? room.houseFeatureLabels.map((label) =>
          isWifiRelatedLabel(label) ? getWifiHouseFeatureLabel() : label
        )
      : [
          getWifiHouseFeatureLabel(),
          getTranslation("property.houseFeature.kitchen"),
          getTranslation("property.houseFeature.furnished"),
        ];

  const siblings: SiblingRoom[] = siblingRooms
    .map((r) => {
      // Same helper the room's own page uses, so a room whose available_from
      // has passed is not labelled "Belegt" here while reading "verfügbar" there.
      const {
        listingAvailable: siblingAvailable,
        nextAvailableDate: siblingFrom,
      } = getListingAvailability({
        available: r.available !== false,
        available_from: r.available_from ?? r.next_available_date,
      });
      const src = firstImageUrl(r.images);
      return {
        id: r.id,
        title: r.title ?? "",
        image: src ? (getImageUrl(src, { width: 720 }) ?? src) : null,
        price: `${formatEuroAmount(Number(r.price) || 0, language)} €`,
        // Size is out of the facts strip for now, so it is out here too —
        // otherwise one page shows a room's m² on the sibling card and omits
        // it on the room itself.
        meta: r.floor ?? "",
        badge: siblingAvailable
          ? language === "de"
            ? "Verfügbar"
            : "Available"
          : siblingFrom
            ? `${language === "de" ? "Verfügbar ab" : "Available from"} ${formatNextAvailableDate(siblingFrom, language)}`
            : language === "de"
              ? "Belegt"
              : "Taken",
        available: siblingAvailable,
      };
    })
    .slice(0, 3);

  // The draft's facts strip. Größe is deliberately out for now: only 4 of 109
  // rooms carry a size, and the team would rather show nothing than a figure
  // that is right for a handful of listings and absent everywhere else.
  //
  // Etage prefers the room's own floor and falls back to the apartment's,
  // resolved server-side because `apartments` is not publicly readable. The
  // resolver applies the same precedence, so the two agree; the local value is
  // read first only so the fact appears on the first paint for the rooms that
  // already carry one, instead of waiting on a round trip.
  const effectiveFloor = pickEffectiveFloor(room.floor, resolvedFloor);
  const facts: RoomFact[] = [];
  if (effectiveFloor) {
    facts.push({
      label: language === "de" ? "Etage" : "Floor",
      value: effectiveFloor,
    });
  }
  // Mitbewohner: the real occupants when the resolver can see them, and the
  // hand-entered `rooms.roommates_count` when it cannot. A room with no
  // apartment_id, or a flat whose tenancies are all historic, returns nobody —
  // falling back keeps today's line rather than printing an empty strip.
  if (flatmates.length > 0) {
    facts.push({
      label: language === "de" ? "Mitbewohner" : "Flatmates",
      value: String(flatmates.length),
      node: <FlatmateChips flatmates={flatmates} de={language === "de"} />,
    });
  } else if (room.roommatesCount != null && room.roommatesCount > 0) {
    facts.push({
      label: language === "de" ? "Mitbewohner" : "Flatmates",
      value:
        language === "de"
          ? `${room.roommatesCount} weitere`
          : `${room.roommatesCount} others`,
    });
  }
  if (facts.length === 0) {
    if (room.city) {
      facts.push({
        label: language === "de" ? "Stadt" : "City",
        value: room.city,
      });
    }
    facts.push({
      label: language === "de" ? "Vertrag" : "Contract",
      value: language === "de" ? "Einzelmietvertrag" : "Your own lease",
    });
  }

  // RoomLocationMap renders nothing without a token or without coordinates, so
  // the same two conditions decide whether the section gets a map or the
  // placeholder — otherwise a missing token leaves the block empty rather than
  // saying so. There is no VITE_MAPBOX_TOKEN locally; tiles come up on Vercel.
  const canPlotMap =
    !!MAPBOX_TOKEN &&
    hasPlottableCoordinates(room.property?.lat, room.property?.lng);

  const moveInValue = listingAvailable
    ? language === "de"
      ? "Sofort"
      : "Now"
    : nextAvailableDate
      ? formatNextAvailableDate(nextAvailableDate, language)
      : language === "de"
        ? "Auf Anfrage"
        : "On request";

  const bookingRows = [
    {
      label:
        language === "de" ? "Miete inkl. Nebenkosten" : "Rent incl. utilities",
      value: `${formatEuroAmount(displayMonthly, language)} €`,
    },
    {
      label:
        language === "de"
          ? "Internet, Strom, Heizung"
          : "Internet, power, heating",
      value: language === "de" ? "inklusive" : "included",
    },
    {
      label: language === "de" ? "Kaution" : "Deposit",
      value: `${formatEuroAmount(displayDeposit, language)} €`,
    },
  ];

  const subtitle = [
    room.city,
    room.buildingName && room.buildingName !== "Property"
      ? room.buildingName
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="min-h-screen bg-white pb-24 lg:pb-0">
      <section
        className="mx-auto w-full max-w-[1328px] px-5 sm:px-8 lg:px-14"
        style={{ paddingTop: "calc(var(--hero-offset) + 2rem)" }}
      >
        <RoomHeader
          de={language === "de"}
          title={room.title}
          city={room.city}
          subtitle={subtitle}
          isFavorited={isLiked}
          onFavorite={toggleLike}
          onShare={handleShare}
          shareCopied={shareCopied}
        />

        <div className="mt-8">
          <RoomGallery
            de={language === "de"}
            images={room.images ?? []}
            alt={room.title}
            onOpen={(index) => {
              setCurrentImageIndex(index);
              setIsLightboxOpen(true);
            }}
          />
        </div>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-16">
          <div className="min-w-0">
            <RoomFacts facts={facts} />

            {(room.description || room.propertyDescription) && (
              <RoomSection
                title={language === "de" ? "Über das Zimmer" : "About the room"}
              >
                <div className="flex flex-col gap-4">
                  {room.description && (
                    <p
                      className="max-w-[68ch] text-[16.5px] leading-[1.7]"
                      style={{ color: MUTED }}
                    >
                      {room.description}
                    </p>
                  )}
                  {room.propertyDescription && (
                    <p
                      className="max-w-[68ch] text-[16.5px] leading-[1.7]"
                      style={{ color: MUTED }}
                    >
                      {room.propertyDescription}
                    </p>
                  )}
                </div>
              </RoomSection>
            )}

            {sidebarHouseFeatures.length > 0 && (
              <RoomSection
                title={language === "de" ? "In der WG" : "In the flat"}
                delay={1}
              >
                <RoomFeatureList items={sidebarHouseFeatures} />
              </RoomSection>
            )}

            {virtualTourSrc && (
              <RoomSection
                title={language === "de" ? "Rundgang" : "Virtual tour"}
                delay={1}
              >
                <div
                  className="relative h-[320px] overflow-hidden rounded-[14px] border sm:h-[420px]"
                  style={{ background: IMAGE_BG, borderColor: LINE }}
                >
                  <iframe
                    title={language === "de" ? "3D-Rundgang" : "3D tour"}
                    src={virtualTourSrc}
                    loading="lazy"
                    allowFullScreen
                    className="absolute inset-0 h-full w-full border-0"
                  />
                </div>
                <p className="mt-4 text-[14px]" style={{ color: MUTED }}>
                  {language === "de"
                    ? "Schau dich per 3D-Rundgang oder im Online-Termin um. Gerne auch vor Ort — nach Vereinbarung."
                    : "Look around in 3D or on a video call. In person too, by arrangement."}
                </p>
              </RoomSection>
            )}

            <RoomSection
              title={language === "de" ? "Lage" : "Location"}
              delay={2}
            >
              <RoomLocation
                de={language === "de"}
                note={
                  language === "de"
                    ? "Ungefährer Standort. Die genaue Adresse erhältst du nach der Bewerbung."
                    : "Approximate location. You get the exact address once you have applied."
                }
              >
                {canPlotMap ? (
                  <RoomLocationMap
                    lat={Number(room.property?.lat)}
                    lng={Number(room.property?.lng)}
                    seed={room.roomId}
                  />
                ) : null}
              </RoomLocation>
            </RoomSection>

            <RoomSection
              title={language === "de" ? "So läuft\u2019s ab" : "How it works"}
              divider={false}
              delay={2}
            >
              <RoomSteps de={language === "de"} />
            </RoomSection>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <RoomBookingCard
              de={language === "de"}
              price={`${formatEuroAmount(displayMonthly, language)} €`}
              moveInLabel={moveInLabel}
              moveInValue={moveInValue}
              rows={bookingRows}
              applyHref={`/booking-confirmation?roomId=${room.roomId}`}
              applyLabel={
                hasAlreadyApplied
                  ? getTranslation("property.applyAlready")
                  : getTranslation("property.apply")
              }
              applyDisabled={hasAlreadyApplied || !canApply}
            />
          </aside>
        </div>
      </section>

      <div className="mt-20">
        <RoomSiblings
          de={language === "de"}
          rooms={siblings}
          allHref={
            room.city
              ? `/rooms/${encodeURIComponent(room.city.toLowerCase())}`
              : "/rooms"
          }
        />
      </div>

      {/* Sticky apply bar on small screens, where the card is far up the page. */}
      <div
        className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-4 border-t bg-white px-5 py-3.5 lg:hidden"
        style={{ borderColor: LINE }}
      >
        <div className="min-w-0">
          <span
            className="block text-[17px] font-semibold"
            style={{ letterSpacing: "-0.02em", color: INK }}
          >
            {formatEuroAmount(displayMonthly, language)} €
          </span>
          <span className="block text-[12.5px]" style={{ color: MUTED }}>
            {language === "de" ? "/ Monat, warm" : "/ month, all in"}
          </span>
        </div>
        {/* A room with no way in (taken, no confirmed date) must not offer an
            application, and the disabled state is a plain element rather than
            a disabled control inside a Link — `pointer-events-none` would
            otherwise let the tap fall through to the Link and reach
            booking-confirmation. */}
        {hasAlreadyApplied || !canApply ? (
          <span
            className="inline-flex h-11 shrink-0 items-center rounded-full px-6 text-[14.5px] font-semibold"
            style={{ background: SAND, color: MUTED }}
          >
            {hasAlreadyApplied
              ? getTranslation("property.applyAlready")
              : getTranslation("property.apply")}
          </span>
        ) : (
          <Link
            to={`/booking-confirmation?roomId=${room.roomId}`}
            className="inline-flex h-11 shrink-0 items-center rounded-full px-6 text-[14.5px] font-semibold text-white"
            style={{ background: INK }}
          >
            {getTranslation("property.apply")}
          </Link>
        )}
      </div>

      {isLightboxOpen && room.images && room.images.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setIsLightboxOpen(false)}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <button
            onClick={() => setIsLightboxOpen(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 p-2"
            aria-label={getTranslation("property.closeLightboxAriaLabel")}
          >
            <X className="w-6 h-6" />
          </button>

          {room.images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) =>
                    prev === 0 ? room.images.length - 1 : prev - 1
                  );
                }}
                className="absolute left-4 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all"
                aria-label={getTranslation("property.previousImageAriaLabel")}
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) =>
                    prev === room.images.length - 1 ? 0 : prev + 1
                  );
                }}
                className="absolute right-4 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all"
                aria-label={getTranslation("property.nextImageAriaLabel")}
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}

          <div className="max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <img
              src={
                getImageUrl(room.images[currentImageIndex], {
                  width: LIGHTBOX_WIDTH,
                }) ?? room.images[currentImageIndex]
              }
              alt={`${room.title} - ${getTranslation("property.imageAlt")} ${currentImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              loading="lazy"
              onClick={(e) => e.stopPropagation()}
              onError={(e) => {
                e.currentTarget.src = room1;
              }}
            />
          </div>

          {room.images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm">
              {currentImageIndex + 1} / {room.images.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyDetail;
